import { camelcaseKeys } from 'src/utils/camelize-keys';
import { decamelizeKeys } from 'src/utils/decamelize-keys';

import {
  associationsToId,
  associationsToIds,
  dateToTimestamp,
  removeReadOnlyFields,
  timestampToDate,
} from '../../utils/fieldNormalizers';
import { normalizeUser } from '../user/normalizers';

import {
  Appointment,
  ConflictCheckParams,
  ConflictCheckResponse,
  EligibilityCheck,
  WaitListEntry,
} from './_types';

const readOnlyFields = [
  'created_at',
  'updated_at',
  'location_name',
  'links',
  'plan_workout_ids',
  'service_name',
  'is_group_class',
  'calendar_id',
  'platform_id',
  'cancellation_fee',
  'noshow_fee',
  'disable_cancellations',
  'primary_trainer_names',
  'secondary_trainer_names',
  'trainer_names',
  'visits',
  'entries',
  'cancel_all',
];

export function normalizeAppointment(appointment): Appointment {
  const camelized = camelcaseKeys(appointment, { deep: true });
  return {
    ...camelized,
    startTime: timestampToDate(camelized.startTime),
    endTime: timestampToDate(camelized.endTime),
  };
}

export function normalizeCheckin(checkin) {
  const camelized = camelcaseKeys(checkin, { deep: true });
  camelized.user = normalizeUser(camelized.user);
  return camelized;
}

export function normalizeEligibilityCheck(elgibilityCheck): EligibilityCheck {
  return camelcaseKeys(elgibilityCheck, { deep: true });
}

export function normalizeWaitListEntry(waitListEntry): WaitListEntry {
  const normalized = camelcaseKeys(waitListEntry, { deep: true });
  // Convert createdAt from Unix timestamp to Date
  if (typeof normalized.createdAt === 'number') {
    normalized.createdAt = timestampToDate(normalized.createdAt);
  }
  return normalized;
}

export function denormalizeWaitListEntry(
  waitListEntry: Partial<WaitListEntry>
) {
  return decamelizeKeys(waitListEntry, { deep: true });
}

export function denormalizeAppointment(appointment: Partial<Appointment>) {
  let decamelized = decamelizeKeys(appointment, { deep: true });

  decamelized = associationsToId(decamelized, ['service', 'location']);
  decamelized = associationsToIds(decamelized, [
    'primary_trainers',
    'secondary_trainers',
  ]);

  removeReadOnlyFields(decamelized, readOnlyFields);

  const result: Record<string, unknown> = {
    ...decamelized,
  };

  // Only include start_time/end_time if they were provided in the input
  if (decamelized.start_time !== undefined) {
    result.start_time = dateToTimestamp(decamelized.start_time);
  }
  if (decamelized.end_time !== undefined) {
    result.end_time = dateToTimestamp(decamelized.end_time);
  }

  // Handle cancelled_at timestamp if present
  if (decamelized.cancelled_at !== undefined) {
    result.cancelled_at = decamelized.cancelled_at
      ? dateToTimestamp(decamelized.cancelled_at)
      : null;
  }

  if (decamelized.expires_on !== undefined && decamelized.expires_on !== null) {
    result.expires_on =
      typeof decamelized.expires_on === 'number'
        ? decamelized.expires_on
        : dateToTimestamp(decamelized.expires_on);
  }

  return result;
}

/**
 * Denormalize conflict check params for API request
 * Matches v2 checkConflicts() in modal-new-appointment.js
 */
export function denormalizeConflictCheckParams(
  params: ConflictCheckParams
): Record<string, unknown> {
  const data: Record<string, unknown> = {
    primary_trainer_ids: params.primaryTrainerIds,
    secondary_trainer_ids: params.secondaryTrainerIds || [],
    start_time: dateToTimestamp(params.startTime),
    end_time: dateToTimestamp(params.endTime),
    appointment_id: params.appointmentId || '',
    location_id: params.locationId || '',
    no_resources: params.noResources || false,
    // service_resource_rules must be JSON stringified per v2 implementation
    service_resource_rules: JSON.stringify(
      (params.serviceResourceRules || []).map((sr) => ({
        resource_id: sr.resourceId,
        usage_behavior: sr.usageBehavior,
        sub_resource_ids: sr.subResourceIds || [],
      }))
    ),
  };

  // Add recurring params if provided (matches v2 scheduled appointment logic)
  if (params.scheduledAppointmentId) {
    data.scheduled_appointment_id = params.scheduledAppointmentId;
  }
  if (params.recurringType) {
    data.recurring_type = params.recurringType;
  }
  if (params.recurringCount) {
    data.recurring_count = params.recurringCount;
  }
  if (params.startAt) {
    data.start_at = params.startAt;
  }
  if (params.endAt) {
    data.end_at = params.endAt;
  }
  if (params.selectedDays) {
    data.selected_days = params.selectedDays;
  }

  return data;
}

/**
 * Normalize conflict check response from API
 * Matches v2 showConflictDialog() response handling in modal-new-appointment.js
 */
export function normalizeConflictCheckResponse(
  response: Record<string, unknown>
): ConflictCheckResponse {
  const conflicts = (response.conflicts || []) as Record<string, unknown>[];

  return {
    hasConflicts: response.has_conflicts as boolean,
    totalConflicts: response.total_conflicts as number,
    conflicts: conflicts.map((c) => ({
      startTime: c.start_time as number,
      endTime: c.end_time as number,
      conflictType: c.conflict_type as 'staff' | 'resource',
      trainerNames: c.trainer_names as string | undefined,
      serviceName: c.service_name as string | undefined,
      resourceName: c.resource_name as string | undefined,
      locationName: c.location_name as string | undefined,
    })),
  };
}
